import { useEffect, useState } from 'react';

import { Box, Button, CardMedia, CircularProgress, Grid, TextField, Tooltip } from '@mui/material';
import _, { flowRight as compose } from 'lodash';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'providers';

import { AxlSimpleBox } from 'AxlMUIComponent/AxlBox';
import AxlRadio from 'AxlMUIComponent/AxlRadio';
import AxlDialog from 'components/AxlDialog';
import JMiniAssignmentBox from 'components/JMiniAssignmentBox';
import acl from 'constants/acl';
import { images } from 'constants/images';
import { STATUS } from 'constants/status';
import styles, * as S from './styles';

function RerouteShipment({
  selectedShipment = {},
  containerStyled = {},
  selectedStop = {},
  keySearch = ['label', 'id', 'zones', 'code'],
  store,
  ...props
}) {
  const { assignmentStore, shipmentStore, permissionStore } = store;

  const [selectedMoveAssignmentId, setSelectedMoveAssignmentId] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { label } = selectedStop;
  const { selectedAssignment } = assignmentStore;
  const selectedAssignmentId = selectedAssignment?.assignment ? selectedAssignment.assignment.id : 0;
  const [rerouteAssignments, setRerouteAssignments] = useState([]);
  const [assignments, setAssignments] = useState([]);

  const [movableAssignmentsCount, setMovableAssignmentsCount] = useState(0);

  useEffect(() => {
    assignmentStore.loadAllAssignments((assignments) => {
      const inProgressAssignments = assignments.filter((a) => a.driver_id && a.is_active && a.status !== STATUS.COMPLETED);
      const pendingAssignments = assignments.filter((a) => a.driver_id && a.status !== STATUS.COMPLETED && !a.is_active);
      const unassignedAssignments = assignments.filter((a) => !a.driver_id && a.status !== STATUS.COMPLETED);

      const processedAssignments = [
        { status: 'UNASSIGNED', assignments: unassignedAssignments },
        { status: 'PENDING', assignments: pendingAssignments },
        { status: 'IN PROGRESS', assignments: inProgressAssignments },
      ];
      setRerouteAssignments(processedAssignments);
      setAssignments(processedAssignments);
    });
  }, [assignmentStore]);

  useEffect(() => {
    let movableAssignments = [];
    rerouteAssignments.forEach((data) => {
      movableAssignments = movableAssignments.concat(data.assignments.filter((as) => as.id !== selectedAssignmentId));
    });
    setMovableAssignmentsCount(movableAssignments.length);
  }, [rerouteAssignments, selectedAssignmentId]);

  const moveShipment = (e) => {
    const { selectedAssignment } = assignmentStore;
    const { selectedShipment } = shipmentStore;

    if (!selectedAssignment || !selectedShipment) {
      return;
    }
    setLoading(true);
    assignmentStore.addShipment(selectedMoveAssignmentId, selectedShipment.id, (res) => {
      if (res.ok && res.status === 200) {
        setTimeout(() => {
          assignmentStore.loadAssignments();
          handleClose();
        }, 1000);
      }
      setLoading(false);
    });
  };

  const selectAssignmentBox = (id) => {
    if (id === selectedMoveAssignmentId) {
      setSelectedMoveAssignmentId(null);
    } else {
      setSelectedMoveAssignmentId(id);
    }
  };

  const onSearch = ({ target: { value } }) => {
    const search = _.debounce((v) => {
      let newItems = [];
      if (v) {
        newItems = _.map(assignments, (data, idx) => {
          const result = data.assignments
            .map((as) => {
              const keySearchMap = keySearch
                .map((key) => _.get(as, key, ''))
                .filter((key) => key)
                .join(' ')
                .toLowerCase();

              return _.includes(keySearchMap, v.toLowerCase()) ? as : null;
            })
            .filter((a) => a);

          return {
            ...data,
            assignments: result,
          };
        }).filter((data) => data.assignments.length);
      } else {
        newItems = assignments;
      }

      setRerouteAssignments(newItems);
    }, 300);

    search(value);
  };

  const handleClose = () => {
    setSelectedMoveAssignmentId(null);
    setRerouteAssignments(assignments);
    setIsOpen(false);
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const isDeniedReroute = permissionStore.isDenied(acl.ASSIGNMENTS.RE_ROUTE);

  if (isDeniedReroute) return null;

  return (
    <S.Container>
      <Tooltip title="Reroute shipment to another assignment">
        <Box>
          <Button
            disableElevation
            variant="outlined"
            size="small"
            disabled={selectedShipment.status === 'DROPOFF_SUCCEEDED'}
            sx={{ p: '7px' }}
            onClick={handleOpen}
          >
            <CardMedia style={styles.footerBtnIcon2} image={images.icon.rerouter} />
          </Button>
        </Box>
      </Tooltip>

      <AxlDialog
        isOpen={isOpen}
        handleClose={handleClose}
        maxWidth="md"
        childrenTitle={
          <Box>
            Reroute shipment <strong>{_.get(label, 'driver_label', '', 'N/A')}</strong>
          </Box>
        }
      >
        <Box sx={styles.container}>
          <Box py={1}>
            <TextField
              onChange={onSearch}
              placeholder={`Search an assignment to move shipment ${_.get(label, 'driver_label', '', 'N/A')} to...`}
              size="small"
              fullWidth
            />
          </Box>
          <AxlSimpleBox>
            <Box p={1} sx={styles.body}>
              {!movableAssignmentsCount && (
                <Box p={1} align="center">
                  No assignments to move
                </Box>
              )}
              {!!movableAssignmentsCount &&
                rerouteAssignments.map((data) =>
                  data.assignments
                    .filter((as) => as.id !== selectedAssignmentId)
                    .map((a, i) => (
                      <Grid container key={`assignment-${a.id}-${i}`} spacing={1} alignItems={'center'}>
                        <Grid>
                          <AxlRadio color={'primary'} checked={selectedMoveAssignmentId === a.id} onClick={() => selectAssignmentBox(a.id)} />
                        </Grid>
                        <Grid>
                          <JMiniAssignmentBox
                            key={a.id}
                            onClick={() => selectAssignmentBox(a.id)}
                            assignment={a}
                            status={rerouteAssignments.status}
                            style={{ opacity: !selectedAssignmentId || selectedMoveAssignmentId === a.id ? 1.0 : 0.5 }}
                          />
                        </Grid>
                        <Grid>
                          <S.StatusText style={{ color: data.color }}>{`- ${data.status}`}</S.StatusText>
                        </Grid>
                      </Grid>
                    )),
                )}
            </Box>
          </AxlSimpleBox>

          <Box display={'flex'} justifyContent={'flex-end'} gap={1}>
            <Button
              disableElevation
              variant="outlined"
              size="small"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              disableElevation
              variant="contained"
              size="small"
              disabled={loading || !selectedMoveAssignmentId}
              onClick={moveShipment}
              startIcon={loading && <CircularProgress size={16} />}
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </AxlDialog>
    </S.Container>
  );
}

export default compose(withRouter, inject('store'), observer)(RerouteShipment);
